import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import moment from 'moment';

import { API_ENDPOINT, SupervisionNoteStatus } from '../../constants';
import { fetchWrapper, toUtcOffsetDisplay } from '../../helpers';
import { getBayValueWithoutPrefix, getSupervisionSearchStr } from '../../utils/common';
import { asDefinedOr } from '../../components/layout/billings/claims/utils';
import {
  ServiceCodes,
  SessionClaimStatus,
} from '../../components/layout/billings/claims/constants';

import type {
  IAssessmentWithClaimStatus,
  IClientAssessment,
  ISessionWithClaimStatus,
} from '../../utils/types';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

const name = 'client_assessments';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<ClientAssessmentState>) =>
    createExtraReducers(builder),
});

export interface ClientAssessmentState {
  list: Array<IClientAssessment>;
  error: any;
  loading: boolean;
  current: IClientAssessment | null;
  assessmentsWithClaimStatus: ISessionWithClaimStatus[];
  assistingAssessmentsWithClaimStatus: ISessionWithClaimStatus[];
}

type ClientAssessmentStateUpdate = {
  [Key in keyof ClientAssessmentState]: { key: Key; data: ClientAssessmentState[Key] };
}[keyof ClientAssessmentState];

function createInitialState(): ClientAssessmentState {
  return {
    list: [],
    loading: false,
    error: null,
    current: null,
    assessmentsWithClaimStatus: [],
    assistingAssessmentsWithClaimStatus: [],
  };
}

function createReducers() {
  return {
    clearState,
    updateState,
  };

  function clearState(state: ClientAssessmentState) {
    state.list = [];
    state.current = null;
    state.error = null;
    state.loading = false;
  }

  function updateState(
    state: ClientAssessmentState,
    action: PayloadAction<ClientAssessmentStateUpdate>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }
}

function getSupervisionNoteStatus(status: string): SupervisionNoteStatus {
  if (status === SupervisionNoteStatus.Started) return SupervisionNoteStatus.Started;
  if (status === SupervisionNoteStatus.Editing) return SupervisionNoteStatus.Editing;
  return SupervisionNoteStatus.Completed;
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/client_assessments`;

  return {
    getList: getList(),
    getAssessmentNote: getAssessmentNote(),
    createAssessmentNote: createAssessmentNote(),
    updateAssessmentNote: updateAssessmentNote(),
    concludeAssessmentNote: concludeAssessmentNote(),
    downloadAssessmentNote: downloadAssessmentNote(),
    getAssessmentsWithClaimStatus: getAssessmentsWithClaimStatus(),
    getAssistingAssessmentsWithClaimStatus: getAssistingAssessmentsWithClaimStatus(),
    deleteAssessmentNote: deleteAssessmentNote(),
    editAssessmentNote: editAssessmentNote(),
    backdateAssessmentNote: backdateAssessmentNote(),
  };

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ params, getAccessTokenSilently, signal }: any) => {
        let baseSearch = `${baseUrl}/?`;
        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }
        const response = await fetchWrapper.get({
          url: baseSearch,
          getAccessTokenSilently,
          signal,
        });
        return response;
      },
    );
  }

  function getAssessmentNote() {
    return createAsyncThunk(
      `${name}/getAssessmentNote`,
      async ({ assessmentNoteId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${assessmentNoteId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createAssessmentNote() {
    return createAsyncThunk(
      `${name}/createAssessmentNote`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }
  function updateAssessmentNote() {
    return createAsyncThunk(
      `${name}/updateAssessmentNote`,
      async ({ data, getAccessTokenSilently, assessmentNoteId }: any) => {
        const response = await fetchWrapper.patch(
          { url: `${baseUrl}/${assessmentNoteId}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }
  function concludeAssessmentNote() {
    return createAsyncThunk(
      `${name}/concludeAssessmentNote`,
      async ({ data, getAccessTokenSilently, assessmentNoteId }: any) => {
        const response = await fetchWrapper.put(
          { url: `${baseUrl}/${assessmentNoteId}/stop/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }
  function downloadAssessmentNote() {
    return createAsyncThunk(
      `${name}/downloadAssessmentNote`,
      async ({ assessmentNoteId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${assessmentNoteId}/download/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getAssessmentsWithClaimStatus() {
    return createAsyncThunk(
      `${name}/getAssessmentsWithClaimStatus`,
      async ({ params, getAccessTokenSilently }: any) => {
        let baseSearch = `${baseUrl}/claim_history/?`;

        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }

        const response = await fetchWrapper.get({ url: baseSearch, getAccessTokenSilently });
        return response;
      },
    );
  }

  function getAssistingAssessmentsWithClaimStatus() {
    return createAsyncThunk(
      `${name}/getAssistingAssessmentsWithClaimStatus`,
      async ({ params, getAccessTokenSilently }: any) => {
        let baseSearch = `${baseUrl}/assisting_assessment_claim_history/?`;

        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }
        const response = await fetchWrapper.get({ url: baseSearch, getAccessTokenSilently });
        return response;
      },
    );
  }

  function deleteAssessmentNote() {
    return createAsyncThunk(
      `${name}/deleteAssessmentNote`,
      async ({ assessmentNoteId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${assessmentNoteId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function editAssessmentNote() {
    return createAsyncThunk(
      `${name}/editAssessmentNote`,
      async ({
        assessmentNoteId,
        getAccessTokenSilently,
      }: {
        assessmentNoteId: number;
        getAccessTokenSilently: () => Promise<string>;
      }) => {
        const response = await fetchWrapper.put({
          url: `${baseUrl}/${assessmentNoteId}/edit/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function backdateAssessmentNote() {
    return createAsyncThunk(
      `${name}/backdateAssessmentNote`,
      async ({
        assessmentNoteId,
        data,
        getAccessTokenSilently,
      }: {
        assessmentNoteId: number;
        data: { date_start: moment.Moment; utc_offset_date_start: number };
        getAccessTokenSilently: () => Promise<string>;
      }) => {
        const response = await fetchWrapper.put(
          { url: `${baseUrl}/${assessmentNoteId}/backdate/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<ClientAssessmentState>) {
  const {
    concludeAssessmentNote,
    createAssessmentNote,
    getList,
    getAssessmentNote,
    updateAssessmentNote,
    downloadAssessmentNote,
    getAssessmentsWithClaimStatus,
    getAssistingAssessmentsWithClaimStatus,
    deleteAssessmentNote,
    editAssessmentNote,
    backdateAssessmentNote,
  } = extraActions;

  builder
    // getList()
    .addCase(getList.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getList.fulfilled,
      (state: ClientAssessmentState, action: PayloadAction<IClientAssessment[]>) => {
        state.loading = false;
        state.list = action.payload;
      },
    )
    .addCase(getList.rejected, (state: ClientAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // getAssessmentNote()
    .addCase(getAssessmentNote.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getAssessmentNote.fulfilled,
      (state: ClientAssessmentState, action: PayloadAction<IClientAssessment>) => {
        state.loading = false;
        state.current = action.payload;
      },
    )
    .addCase(getAssessmentNote.rejected, (state: ClientAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createAssessmentNote()
    .addCase(createAssessmentNote.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createAssessmentNote.fulfilled,
      (state: ClientAssessmentState, action: PayloadAction<IClientAssessment>) => {
        state.loading = false;
        state.current = action.payload;
        toast.success('Assessment note created');
      },
    )
    .addCase(createAssessmentNote.rejected, (state: ClientAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateAssessmentNote()
    .addCase(updateAssessmentNote.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateAssessmentNote.fulfilled,
      (state: ClientAssessmentState, action: PayloadAction<IClientAssessment>) => {
        state.loading = false;
        state.current = action.payload;
      },
    )
    .addCase(updateAssessmentNote.rejected, (state: ClientAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // concludeAssessmentNote()
    .addCase(concludeAssessmentNote.pending, (state: ClientAssessmentState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(concludeAssessmentNote.fulfilled, (state: ClientAssessmentState) => {
      state.loading = false;
      toast.success('Assessment note has been submitted');
    })
    .addCase(concludeAssessmentNote.rejected, (state: ClientAssessmentState, action: any) => {
      state.loading = false;
      state.error = action.error;
      toast.error(action.error.message);
    })
    // downloadAssessmentNote()
    .addCase(downloadAssessmentNote.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(downloadAssessmentNote.fulfilled, (state: ClientAssessmentState, _action: any) => {
      state.loading = false;
    })
    .addCase(downloadAssessmentNote.rejected, (state: ClientAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getAssessmentsWithClaimStatus()
    .addCase(getAssessmentsWithClaimStatus.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getAssessmentsWithClaimStatus.fulfilled,
      (state: ClientAssessmentState, action: PayloadAction<IAssessmentWithClaimStatus[]>) => {
        state.loading = false;
        state.assessmentsWithClaimStatus = action.payload.map(
          (assessment: IAssessmentWithClaimStatus) => {
            return {
              id: assessment.id,
              date: moment(assessment.date_start).format(),
              status:
                assessment.claim_history && assessment.claim_history?.length > 0
                  ? assessment.claim_history[0].status
                  : SessionClaimStatus.Unclaimed,
              selected: false,
              readonly: Boolean(assessment.claim_id),
              duration: assessment.duration ?? 0,
              provider_name: `${assessment.provider.first_name} ${assessment.provider.last_name} `,
              location: assessment.location,
              provider_id: assessment.provider.id,
              author_id: assessment.provider.id, // assessment provider is the author
              bay: getBayValueWithoutPrefix(assessment.bay),
              timeZone: assessment.utc_offset_date_start
                ? toUtcOffsetDisplay(asDefinedOr(assessment.utc_offset_date_start, 0))
                : '0:0',
              sessionStatus: getSupervisionNoteStatus(assessment.status),
              procedureCode: ServiceCodes.AssessmentCode,
            };
          },
        );
      },
    )
    .addCase(
      getAssessmentsWithClaimStatus.rejected,
      (state: ClientAssessmentState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(action.error.message, {
          toastId: 'client-assessments-with-claim-status-error',
        });
      },
    )
    // getAssistingAssessmentsWithClaimStatus()
    .addCase(getAssistingAssessmentsWithClaimStatus.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getAssistingAssessmentsWithClaimStatus.fulfilled,
      (state: ClientAssessmentState, action: PayloadAction<IAssessmentWithClaimStatus[]>) => {
        state.loading = false;
        state.assistingAssessmentsWithClaimStatus = action.payload.map(
          (assessment: IAssessmentWithClaimStatus) => {
            return {
              id: assessment.id,
              date: moment(assessment.date_start).format(),
              status:
                assessment.claim_history && assessment.claim_history?.length > 0
                  ? assessment.claim_history[0].status
                  : SessionClaimStatus.Unclaimed,
              selected: false,
              readonly: Boolean(assessment.claim_id),
              duration: assessment.duration ?? 0,
              provider_name: `${assessment.provider.first_name} ${assessment.provider.last_name} `,
              location: assessment.location,
              provider_id: assessment.provider.id,
              author_id: assessment.provider.id, // assessment provider is the author
              bay: getBayValueWithoutPrefix(assessment.bay),
              timeZone: assessment.utc_offset_date_start
                ? toUtcOffsetDisplay(asDefinedOr(assessment.utc_offset_date_start, 0))
                : '0:0',
              sessionStatus: getSupervisionNoteStatus(assessment.status),
              procedureCode: ServiceCodes.AssistingAssessmentCode,
            };
          },
        );
      },
    )
    .addCase(
      getAssistingAssessmentsWithClaimStatus.rejected,
      (state: ClientAssessmentState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(action.error.message, {
          toastId: 'client-assessments-with-claim-status-error',
        });
      },
    )
    // deleteAssessmentNote()
    .addCase(deleteAssessmentNote.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteAssessmentNote.fulfilled, (state: ClientAssessmentState) => {
      state.current = null;
      state.loading = false;
      toast.success('Assessment notes deleted successfully');
    })
    .addCase(deleteAssessmentNote.rejected, (state: ClientAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // editAssessmentNote()
    .addCase(editAssessmentNote.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(editAssessmentNote.fulfilled, (state: ClientAssessmentState) => {
      state.loading = false;
      state.error = null;
      toast.success('Assessment note is now editable');
    })
    .addCase(editAssessmentNote.rejected, (state: ClientAssessmentState, action) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // backdateAssessmentNote()
    .addCase(backdateAssessmentNote.pending, (state: ClientAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(backdateAssessmentNote.fulfilled, (state: ClientAssessmentState) => {
      state.loading = false;
      state.error = null;
      toast.success('The start date of the assessment note has been updated');
    })
    .addCase(backdateAssessmentNote.rejected, (state: ClientAssessmentState, action) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    });
}

export const clientAssessmentActions = { ...slice.actions, ...extraActions };
export const clientAssessmentReducer = slice.reducer;
